export const SUPPORTED_LOCALES = ["en", "es", "pt", "fr", "de", "ja", "hi", "zh", "ko", "id"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/**
 * All locale files now contain real translations for the full UiStrings
 * scope (nav, trustBadges, common, footer, contact) -- es/pt/fr/de/ja/hi.
 * The /es, /pt, /fr, /de, /ja, /hi routes work fine regardless (same
 * content, different URL), which is why they stay enabled even while this
 * flag is false. But hreflang tags tell search engines "this is a genuine
 * translated alternate of this page" -- flip this to true once you're
 * comfortable with translation quality across all six locales. Japanese
 * and Hindi in particular are worth a native speaker's review before
 * activating, same as every other locale here -- these are strong
 * first-pass translations, not a substitute for that review.
 */
export const TRANSLATIONS_LIVE = true;

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Espanol",
  pt: "Portugues",
  fr: "Francais",
  de: "Deutsch",
  ja: "\u65e5\u672c\u8a9e",
  hi: "\u0939\u093f\u0928\u094d\u0926\u0940",
  zh: "\u4e2d\u6587",
  ko: "\ud55c\uad6d\uc5b4",
  id: "Bahasa Indonesia",
};

/**
 * English has no URL prefix (it's the default). Other supported locales
 * are prefixed, e.g. /es/tool/merge-pdf. Detects the locale from a raw
 * pathname and returns both the locale and the path with any locale
 * prefix stripped off, so route matching underneath can stay unaware of
 * locale entirely.
 */
export function parseLocaleFromPath(pathname: string): { locale: Locale; rest: string } {
  const segments = pathname.split("/");
  const first = segments[1];
  if (first && (SUPPORTED_LOCALES as readonly string[]).includes(first) && first !== DEFAULT_LOCALE) {
    const rest = "/" + segments.slice(2).join("/");
    return { locale: first as Locale, rest: rest === "/" ? "/" : rest.replace(/\/$/, "") || "/" };
  }
  return { locale: DEFAULT_LOCALE, rest: pathname };
}

/** Builds the path for `rest` (an unprefixed, e.g. "/tool/merge-pdf" path)
 * in the given locale. English gets no prefix. */
export function buildLocalizedPath(locale: Locale, rest: string): string {
  if (locale === DEFAULT_LOCALE) return rest;
  return rest === "/" ? `/${locale}` : `/${locale}${rest}`;
}
